import axios from 'axios';

/**
 * HTTP-error predicates shared by TanStack `retry` / `refetchInterval`
 * callbacks.
 *
 * ## Why a polled read needs this (AUT-328)
 *
 * The SPA's permission set comes off the JWT and is up to 15 minutes stale, so
 * an operator whose `process_payouts` was revoked mid-session keeps rendering a
 * page whose query now 403s. Left alone, a tab on a desk turns that into an
 * endless loop: the poll re-fires every 30 seconds, the global `retry: 1`
 * doubles each round, and the shared 403 toast re-opens every time (the toast
 * bus has a fixed id but no dedupe — its subscriber re-opens the snackbar on
 * every publish).
 *
 * A permission failure is not a transient failure. Nothing about retrying a
 * 403, a 404 or a 400 can make it succeed, so a polled query stops on them and
 * renders the state inline instead.
 */

/** HTTP status of an axios error, or `null` for network / non-HTTP failures. */
export const httpStatusOf = (error: unknown): number | null => {
  if (!axios.isAxiosError(error)) return null;
  return error.response?.status ?? null;
};

/**
 * Statuses that a retry / poll can never turn into a success.
 *
 * Every 4xx EXCEPT the two that are explicitly "try again": `408` (the server
 * gave up waiting for the request) and `429` (rate limited — a later attempt is
 * exactly the remedy). 5xx and network errors stay retryable: the API being
 * down or restarting is precisely the case the poll is meant to ride out.
 *
 * A non-axios error (a thrown `Error`, an aborted signal) is NOT non-retryable
 * — we only stop on a status the server actually stated.
 */
export const isNonRetryableStatus = (error: unknown): boolean => {
  const status = httpStatusOf(error);
  if (status === null) return false;
  if (status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
};

/** `true` when the failure is specifically "you may not read this". */
export const isForbiddenError = (error: unknown): boolean => httpStatusOf(error) === 403;

/**
 * The server's stable error `code` (`DomainError`), or `null` when the failure
 * carries none. Moved from `companies/_lib/companyErrors` (AUT-542) — every
 * section that maps codes to texts reads it from here.
 */
export const errorCodeOf = (err: unknown): string | null => {
  if (!axios.isAxiosError(err)) return null;
  const code = (err.response?.data as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? code : null;
};
